import { ICalendarEvent } from './types';

export enum WeekDays {
	SUNDAY = 0,
	MONDAY = 1,
	TUESDAY = 2,
	WEDNESDAY = 3,
	THURSDAY = 4,
	FRIDAY = 5,
	SATURDAY = 6
}

export enum MonthNames {
	JANUARY = 0,
	FEBRUARY = 1,
	MARCH = 2,
	APRIL = 3,
	MAY = 4,
	JUNE = 5,
	JULY = 6,
	AUGUST = 7,
	SEPTEMBER = 8,
	OCTOBER = 9,
	NOVEMBER = 10,
	DECEMBER = 11,
}

export enum DateDiffPrecision {
	MINUTES = 0,
	HOURS = 1,
	DAYS = 2,
	WEEKS = 3
}

export const getFirstDayOfMonth = (dt: Date) => {
	return new Date(dt.getFullYear(), dt.getMonth());
};

export const getLastDayOfMonth = (dt: Date) => {
	const next = new Date(dt.getFullYear(), dt.getMonth() + 1);
	return new Date(next.setDate(next.getDate() - 1));
};

export const getFirstDayOfWeek = (dt: Date, firstDay: WeekDays = WeekDays.MONDAY) => {
	const date = new Date(dt);
	const diff = date.getDay() - firstDay;
	if (diff === 0) return date;
	const offset = diff > 0 ? diff : 7 + diff;
	return new Date(date.setDate(date.getDate() - offset));
};

export const getLastDayOfWeek = (dt: Date, firstDay: WeekDays = WeekDays.MONDAY) => {
	const first = getFirstDayOfWeek(dt, firstDay);
	return new Date(first.setDate(first.getDate() + 6));
};

export const dayRange = (from: Date, to: Date) => {
	// need to include the to value as part of the range returned
	const inclusiveEndDate = new Date(to);
	inclusiveEndDate.setDate(to.getDate() + 1);
	const count = dateDiff(from, inclusiveEndDate);
	return new Array(count).fill('').map((_, i) => {
		const d = new Date(`${from.getFullYear()}-${pad(from.getMonth() + 1)}-${pad(from.getDate())}T00:00:00Z`);
		d.setDate(d.getDate() + i);
		return d;
	});
};

export const dateEquals = (d1: Date, d2: Date) => {
	return dateDiff(d1, d2) === 0;
};

export const dateDiff = (start: Date, end: Date, precision = DateDiffPrecision.DAYS): number => {
	const delta = (end.getTimezoneOffset() - start.getTimezoneOffset()) * 60 * 1000;
	const diff = (end.valueOf() - start.valueOf() - delta) / 1000 / 60;
	switch (precision) {
		case DateDiffPrecision.MINUTES:
			return Math.floor(diff);
		case DateDiffPrecision.HOURS:
			return Math.floor(diff / 60);
		case DateDiffPrecision.DAYS:
		default:
			return Math.floor(diff / 60 / 24);
		case DateDiffPrecision.WEEKS:
			return Math.floor(diff / 60 / 24 / 7);
	}
};

export const dateIncrement = (date: Date, increment: number) => {
	const newDate = new Date(date);
	newDate.setDate(newDate.getDate() + increment);
	return newDate;
};

/**
 * @param date A date string in format YYYY-MM-DD
 * @returns A date object representing the same date in local time
 */
export const dateStringToDate = (date: string): Date => {
	// adding 'T00:00:00' means the date string is converted with local time zone
	// according to ISO date string standards
	// if we used 'T00:00:00Z' then it would be converted as a Zulu (UTC) date
	// which would mean that the absolute date could differ from the server date by +/- 1 day
	return new Date(date + 'T00:00:00');
};

export enum DAY_BORDER_TYPES {
	LEFT,
	TOP,
	BOTH,
	NONE,
	BOTTOM,
	BOTTOM_RIGHT
}

/**
 * For a given set of dates, and a selected month, retrieve an array of the additional borders that need to
 * be applied to each day cell to complete it (each gets right and bottom by default)
 * @param currentMonth {number} - The index of the current month (0-based)
 * @param days {Date[]} - The days that will be shown on the calendar's view, including any that overspill the current month.
 *
 * @returns borders {DAY_BORDER_TYPES[]} - The additional borders that each day requires.
 */
export const getDayCellAdditionalBorders = (currentMonth: number, days: Date[]): DAY_BORDER_TYPES[] => {
	const lastOverspillIdx = days.findIndex((d) => {
		const day = utcNormalisedDate(d);
		return day.getMonth() === currentMonth;
	}) - 1;

	return days.map((d, idx) => {
		// Initial overspills
		if (idx < lastOverspillIdx) {
			return DAY_BORDER_TYPES.BOTTOM;
		}

		if (idx === lastOverspillIdx) {
			return DAY_BORDER_TYPES.BOTTOM_RIGHT;
		}

		// First row in current month
		if (idx < 7 && idx > lastOverspillIdx) {
			const day = utcNormalisedDate(d);
			// First of month
			if (day.getDate() === 1) {
				// Last overspill day adds the RH border to close this first day
				return lastOverspillIdx < 0 ? DAY_BORDER_TYPES.BOTH : DAY_BORDER_TYPES.TOP;
			} else {
				return DAY_BORDER_TYPES.TOP;
			}
		}

		// 2nd row
		if (idx >= 7 && idx <= 13) {
			// First in row
			if (idx % 7 === 0) {
				return DAY_BORDER_TYPES.LEFT;
			} else {
				return DAY_BORDER_TYPES.NONE;
			}
		}

		// First of each subsequent row
		if (idx % 7 === 0) {
			return DAY_BORDER_TYPES.LEFT;
		}

		return DAY_BORDER_TYPES.NONE;
	});
};

export const isDateSelected = (selectedDays: Date[], day: Date): boolean => {
	const idx = selectedDays.findIndex((selected) => dateEquals(selected, day));
	return idx > -1;
};

export const dateIsInRange = (date: Date, range: [Date, Date]) => {
	const [from, to] = range;
	const time = Math.round(date.getTime() / 1000 / 60 / 60 / 24);
	return (time) >= Math.round(from.getTime() / 1000 / 60 / 60 / 24) && time <= Math.round(to.getTime() / 1000 / 60 / 60 / 24);
};

export const isDayBlocked = (eventsToday: ICalendarEvent[], currentDay: Date): boolean =>
	(eventsToday).filter((e) => e.status === 'blocked' && !dateEquals(e.end, currentDay)).length > 0;

export const eventFinishesDateAvailable = (eventsToday: ICalendarEvent[], currentDay: Date): boolean => {
	if (eventsToday.length === 0) return true;
	return eventsToday.length < 2 && dateDiff(eventsToday[0].end, currentDay) === 0;
};

const pad = (n: number) => {
	return n < 10 ? '0' + n : n;
};

export const utcNormalisedDate = (date: Date): Date => {
	let offset = date.getTimezoneOffset();
	offset = offset < 0 ? 0 : offset;
	return new Date(date.getTime() + offset * 60000);
};
